import React, { useEffect, useMemo, useState } from "react"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"

import { POLICIES_CONFIG } from "../../../config/policies.config"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { policiesGetReportsAggregationsStatusCodes } from "../../../store/thunks/policies.thunks"
import type { IPoliciesAggregatedChartValues } from "../../../types/objects.types"
import { PoliciesAggregationsChartComponent } from "../policiesAggregationsChart.component"
import { PoliciesAggregationsChartLegendComponent } from "../policiesAggregationsChartLegend.component"

export const ReportsStatusCodesVisualizationComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { reportsFilters, reportsAggregationsStatusCodes } =
        useAppSelector(policiesSelector)

    const [hoveredLabel, setHoveredLabel] = useState<string>()

    const isLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isReportsAggregationsStatusCodesLoading()
    }, [runtimeState.isLoading])

    const aggregatedCalculations = useMemo<
        IPoliciesAggregatedChartValues[]
    >(() => {
        const result = [] as IPoliciesAggregatedChartValues[]

        for (const config of POLICIES_CONFIG.reportsStatusCodesAggregations) {
            const value = reportsAggregationsStatusCodes
                ? config.aggregationRules
                    ? PoliciesListHelper.getStatusCodeAggregationValue(
                          reportsAggregationsStatusCodes,
                          config,
                      )
                    : // If aggregationRules not set it's everything that left: total - sum(previous)
                      reportsAggregationsStatusCodes.total -
                      result.reduce((memo, a) => memo + a.value, 0)
                : 0

            result.push({
                label: config.label,
                color: config.color,
                value,
                percentage: UtilHelper.getPercentageValue(
                    value,
                    reportsAggregationsStatusCodes?.total,
                ),
            })
        }

        return result
    }, [JSON.stringify(reportsAggregationsStatusCodes)])

    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        const thunkPromise = dispatch(
            policiesGetReportsAggregationsStatusCodes({
                practice: selectedPracticeRole.practice,
                payload: {
                    filters: reportsFilters,
                },
            }),
        )

        return () => {
            thunkPromise.abort()
        }
    }, [selectedPracticeRole?.practice.id, JSON.stringify(reportsFilters)])

    return (
        <div
            data-produktly="reports-general-status-overview"
            className={`
                relative min-h-[550px]
                ${props.className}
            `}
        >
            {isLoading ? (
                <div
                    className="
                        absolute z-10 w-full h-full bg-brand-whiteTransparent85
                        flex items-center justify-center
                    "
                >
                    <SpinnerElement />
                </div>
            ) : null}

            {reportsAggregationsStatusCodes && (
                <div className="py-[40px] px-[24px]">
                    <PrimaryText
                        centered
                        size={29}
                        lineHeight={34}
                        weight="bold"
                    >
                        Total Checks: {reportsAggregationsStatusCodes.total}
                    </PrimaryText>

                    <PoliciesAggregationsChartComponent
                        className="mt-[16px] mx-auto"
                        data={aggregatedCalculations}
                        hoveredLabel={hoveredLabel}
                        widthPercentage={80}
                    />

                    <PoliciesAggregationsChartLegendComponent
                        className="mt-[16px]"
                        data={aggregatedCalculations}
                        label="Status"
                        onLabelHover={setHoveredLabel}
                    />
                </div>
            )}
        </div>
    )
}
